"use client";

import type { ReactNode } from "react";

import { button as buttonStyles } from "@heroui/theme";
import Link from "next/link";

interface MovieStatusMessageProps {
  icon: string;
  title: string;
  description: string;
  /** Primary action button (e.g. retry). */
  action?: { label: string; onPress: () => void };
  /** Link shown next to the action, e.g. back to the movie list. */
  link?: { href: string; label: string };
  children?: ReactNode;
}

/** Centered full-page message for not-found and error states. */
export function MovieStatusMessage({
  icon,
  title,
  description,
  action,
  link,
  children,
}: MovieStatusMessageProps) {
  return (
    <div className="flex min-h-[60vh] w-full flex-col items-center justify-center py-8 text-center">
      <div aria-hidden="true" className="mb-4 text-6xl">
        {icon}
      </div>
      <h1 className="mb-2 text-2xl font-bold">{title}</h1>
      <p className="mb-6 max-w-md text-default-600">{description}</p>
      <div className="flex flex-wrap justify-center gap-3">
        {action && (
          <button
            className={buttonStyles({ color: "primary" })}
            type="button"
            onClick={action.onPress}
          >
            {action.label}
          </button>
        )}
        {link && (
          <Link
            className={buttonStyles({
              color: action ? "default" : "primary",
              variant: action ? "flat" : "solid",
            })}
            href={link.href}
          >
            {link.label}
          </Link>
        )}
      </div>
      {children}
    </div>
  );
}
